import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check, Copy, Download, FileText, KeyRound, Loader2, Pencil, Plug, Plus, Power,
  RefreshCw, ShieldAlert, Trash2, X,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from './components/ConfirmDialog';
import PageSizeSelect from './components/PageSizeSelect';
import { AdminError, AdminSkeleton } from './components/PageState';
import { formatDateTime, formatMoney } from './lib/formatters';

/**
 * Operasyon → Entegrasyon API.
 *
 * Dış sistemlerin (CRM, raporlama) `/api/ext/v1` uçlarına girdiği anahtarlar
 * burada üretilir ve denetlenir. Anahtar bütün üyelerin kişisel verisini
 * okuyabilir ve yetkisine göre bakiye/bonus hareketi doğurur; bu yüzden ekran
 * `admin.manage` iznine bağlıdır.
 *
 * Sır yalnızca üretildiği ya da yenilendiği yanıtta gelir ve bu ekranda bir
 * kez gösterilir; sayfadan çıkınca kaybolur. Her istek (reddedilenler dahil)
 * alttaki "Son İstekler" listesinde görünür — "imza hatası alıyoruz"
 * şikâyetinin cevabı oradadır.
 *
 * `entry_domain.read` (güncel giriş adresi) tek başına verilirse IP listesi
 * isteğe bağlıdır: onu okuyan Cloudflare Worker'ın sabit çıkış IP'si yoktur.
 *
 * Dış ekibe verilen doküman hazır bir dosya değildir: yönetici özellikleri
 * seçer, sunucu belgeyi o anda yalnızca onları anlatacak biçimde üretir
 * (`GET /admin/integration-keys/docs?features=…`). Anahtarın satırındaki
 * "Doküman" düğmesi seçimi o anahtarın yetkilerinden yapar.
 */

const emptyForm = {
  name: '',
  scopes: ['read'],
  ip_allowlist: '',
  rate_limit_per_minute: 120,
  daily_credit_limit: 0,
};

const ERROR_LABELS = {
  missing_credentials: 'İmza başlıkları eksik',
  unknown_key: 'Anahtar tanınmadı',
  key_disabled: 'Anahtar pasif',
  bad_timestamp: 'Zaman damgası hatalı',
  stale_timestamp: 'Zaman damgası eski',
  bad_signature: 'İmza tutmadı',
  ip_not_allowed: 'IP izinli değil',
  scope_missing: 'Yetki yok',
  rate_limited: 'Dakikalık sınır aşıldı',
  daily_limit_exceeded: 'Günlük tutar sınırı',
  idempotency_key_required: 'Idempotency-Key yok',
  client_not_found: 'Üye bulunamadı',
  bonus_blocked: 'Üyede bonus yasağı',
  validation_error: 'Eksik/hatalı alan',
  rejected: 'İş kuralı reddi',
};

const copyText = async (text, toast, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast({ title: `${label} kopyalandı` });
  } catch (copyError) {
    toast({ title: 'Kopyalanamadı', description: 'Metni elle seçip kopyalayın.' });
  }
};

// Bir anahtarın yetkilerinin açtığı doküman özellikleri.
const featuresForScopes = (catalog, scopes) => catalog
  .filter((feature) => feature.scope && scopes.includes(feature.scope))
  .map((feature) => feature.key);

const DOC_GROUPS = [
  { title: 'Okuma', match: (feature) => feature.scope === 'read' },
  { title: 'İşlem (yazma yetkisi)', match: (feature) => isWriteScope(feature.scope || '') },
  {
    title: 'Giriş adresi',
    match: (feature) => feature.scope !== 'read' && !isWriteScope(feature.scope || ''),
  },
];

const errorText = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

// Liste olmadan verilebilen yetkiler (backend `IP_OPTIONAL_SCOPES` ile aynı).
const IP_OPTIONAL_SCOPES = ['entry_domain.read'];
const ipListOptional = (scopes) => scopes.length > 0
  && scopes.every((scope) => IP_OPTIONAL_SCOPES.includes(scope));
const isWriteScope = (scope) => scope.endsWith('.write');

const KeyForm = ({ initial, scopes, limits, busy, onCancel, onSubmit }) => {
  const [form, setForm] = useState(initial);
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const toggleScope = (key) => update(
    'scopes',
    form.scopes.includes(key)
      ? form.scopes.filter((item) => item !== key)
      : [...form.scopes, key],
  );
  const writes = form.scopes.some(isWriteScope);
  const ipOptional = ipListOptional(form.scopes);

  return (
    <form
      className="admin-card p-5 space-y-4"
      onSubmit={(event) => { event.preventDefault(); onSubmit(form); }}
    >
      <div className="font-semibold">{initial.id ? 'Anahtarı düzenle' : 'Yeni anahtar'}</div>
      <label className="block text-sm" htmlFor="integration-name">
        <span className="text-slate-400 text-xs">Ad (kayıtlarda “API: ad” olarak görünür)</span>
        <input
          id="integration-name"
          className="admin-input mt-1"
          required
          maxLength={64}
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          placeholder="ör. CRM…"
          autoComplete="off"
          spellCheck={false}
        />
      </label>

      <fieldset>
        <legend className="text-slate-400 text-xs mb-2">Yetkiler</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {scopes.map((scope) => (
            <label
              key={scope.key}
              className="flex items-start gap-2 rounded-lg p-3 cursor-pointer"
              style={{ background: 'var(--admin-surface-hover)' }}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={form.scopes.includes(scope.key)}
                onChange={() => toggleScope(scope.key)}
              />
              <span>
                <span className="block text-sm font-semibold">{scope.label}</span>
                <span className="block text-xs text-slate-400">{scope.description}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="block text-sm" htmlFor="integration-ips">
        <span className="text-slate-400 text-xs">
          {ipOptional
            ? 'İzinli IP adresleri (isteğe bağlı: yalnızca güncel giriş adresi okunur; Cloudflare Worker için boş bırakın)'
            : 'İzinli IP adresleri (her satıra bir IP ya da CIDR; zorunlu)'}
        </span>
        <textarea
          id="integration-ips"
          className="admin-input mt-1 font-mono"
          rows={3}
          required={!ipOptional}
          value={form.ip_allowlist}
          onChange={(event) => update('ip_allowlist', event.target.value)}
          autoComplete="off"
          spellCheck={false}
          placeholder={'203.0.113.10\n198.51.100.0/28'}
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm" htmlFor="integration-rate">
          <span className="text-slate-400 text-xs">Dakikalık istek sınırı</span>
          <input
            id="integration-rate"
            type="number"
            className="admin-input mt-1"
            min={limits.rate_limit_min}
            max={limits.rate_limit_max}
            value={form.rate_limit_per_minute}
            onChange={(event) => update('rate_limit_per_minute', Number(event.target.value))}
          />
        </label>
        <label className="block text-sm" htmlFor="integration-credit">
          <span className="text-slate-400 text-xs">
            Günlük tutar sınırı, ₺ (nakit + bonus + freebet; 0 = sınırsız)
          </span>
          <input
            id="integration-credit"
            type="number"
            min={0}
            step="0.01"
            className="admin-input mt-1"
            value={form.daily_credit_limit}
            onChange={(event) => update('daily_credit_limit', event.target.value)}
          />
        </label>
      </div>

      {writes && (
        <p className="text-xs flex gap-2 items-start text-amber-400">
          <ShieldAlert size={14} className="shrink-0 mt-0.5" />
          Bu anahtar üyelerin bakiyesini, bonusunu ya da bonus yasağını değiştirebilir.
          Bakiye ve bonus için günlük tutar sınırı koymanız önerilir.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={busy || !form.scopes.length} className="admin-btn-primary flex items-center gap-2 disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />}
          {initial.id ? 'Kaydet' : 'Anahtar Oluştur'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className="admin-btn-secondary">Vazgeç</button>
      </div>
    </form>
  );
};

// Kopyalanan metin etiketiyle birlikte gider: dış ekibe yapıştırılan iletide
// hangi değerin ne olduğu yazmalıdır. Sırrın etiketi İngilizce "Secret"tir —
// karşı taraf onu yapılandırmasında bu adla arar.
const secretFields = (reveal) => [
  { label: 'Anahtar kimliği (X-Api-Key)', copyLabel: 'Anahtar kimliği (X-Api-Key)', value: reveal.key_id },
  { label: 'Sır (secret)', copyLabel: 'Secret', value: reveal.secret },
];

const fieldText = (field) => `${field.copyLabel}\n${field.value}`;

const SecretCard = ({ reveal, toast, onClose, onDocs }) => {
  const fields = secretFields(reveal);
  return (
    <div
      className="admin-card p-5 space-y-3"
      style={{ borderColor: 'var(--accent-primary)' }}
      role="status"
    >
      <div className="flex items-center gap-2 font-semibold">
        <KeyRound size={16} style={{ color: 'var(--accent-primary)' }} aria-hidden="true" />
        {reveal.rotated ? 'Yeni sır üretildi' : 'Anahtar oluşturuldu'} — {reveal.name}
      </div>
      <p className="text-xs text-amber-400">
        Sır yalnızca şimdi gösterilir; bu sayfadan çıkınca bir daha okunamaz.
        Güvenli bir kanaldan dış ekibe iletin.
        {reveal.rotated && ' Eski sır 24 saat daha geçerlidir.'}
      </p>
      {fields.map((field) => (
        <div key={field.label}>
          <div className="text-xs text-slate-400 mb-1">{field.label}</div>
          <div className="flex items-stretch gap-2">
            <code className="admin-input font-mono text-xs break-all flex-1 min-w-0" translate="no">{field.value}</code>
            <button
              type="button"
              className="admin-btn-secondary shrink-0 flex items-center gap-1.5"
              onClick={() => copyText(fieldText(field), toast, field.copyLabel)}
              aria-label={`${field.label} kopyala`}
            >
              <Copy size={14} aria-hidden="true" /> Kopyala
            </button>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="admin-btn-primary flex items-center gap-2"
          onClick={() => copyText(fields.map(fieldText).join('\n\n'), toast, 'Anahtar bilgileri')}
        >
          <Copy size={14} aria-hidden="true" /> Tümünü Kopyala
        </button>
        <button type="button" className="admin-btn-secondary flex items-center gap-2" onClick={onClose}>
          <Check size={14} aria-hidden="true" /> Kaydettim, kapat
        </button>
        {onDocs && (
          <button type="button" className="admin-btn-secondary flex items-center gap-2" onClick={onDocs}>
            <FileText size={14} aria-hidden="true" /> Bu anahtarın dokümanını indir
          </button>
        )}
      </div>
    </div>
  );
};

const DocBuilder = ({ catalog, keys, busy, onClose, onDownload }) => {
  const [selected, setSelected] = useState([]);
  const toggle = (key) => setSelected((prev) => (
    prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
  ));
  const fromKey = (keyId) => {
    const key = keys.find((item) => item.id === keyId);
    if (key) setSelected(featuresForScopes(catalog, key.scopes));
  };

  return (
    <div className="admin-card p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="font-semibold">API dokümanı oluştur</div>
          <p className="text-xs text-slate-400 mt-1">
            Dış ekibin kullanacağı özellikleri seçin; doküman yalnızca onları anlatır
            (uçlar, alanlar, hata kodları ve kurallar). Dosya HTML olarak iner, tarayıcıda
            açılıp “PDF olarak kaydet” ile PDF yapılabilir. Panelin adresi
            ({window.location.host}) dokümana temel adres olarak yazılır.
          </p>
        </div>
        <button type="button" className="admin-btn-secondary" onClick={onClose} aria-label="Doküman oluşturmayı kapat">
          <X size={14} aria-hidden="true" />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {keys.length > 0 && (
          <select
            className="admin-input admin-input-w-md"
            aria-label="Anahtarın yetkilerine göre seç"
            value=""
            onChange={(event) => fromKey(event.target.value)}
          >
            <option value="">Anahtarın yetkilerine göre seç…</option>
            {keys.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        )}
        <button type="button" className="admin-btn-secondary" onClick={() => setSelected(catalog.map((feature) => feature.key))}>
          Tümünü seç
        </button>
        <button type="button" className="admin-btn-secondary" onClick={() => setSelected([])} disabled={!selected.length}>
          Temizle
        </button>
      </div>

      {DOC_GROUPS.map((group) => {
        const items = catalog.filter(group.match);
        if (!items.length) return null;
        return (
          <fieldset key={group.title}>
            <legend className="text-slate-400 text-xs mb-2">{group.title}</legend>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((feature) => (
                <label
                  key={feature.key}
                  className="flex items-start gap-2 rounded-lg p-3 cursor-pointer"
                  style={{ background: 'var(--admin-surface-hover)' }}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected.includes(feature.key)}
                    onChange={() => toggle(feature.key)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{feature.label}</span>
                    <span className="block text-xs text-slate-400" translate="no">
                      {feature.scope}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        );
      })}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="admin-btn-primary flex items-center gap-2 disabled:opacity-50"
          disabled={busy || !selected.length}
          onClick={() => onDownload(selected)}
        >
          {busy
            ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            : <Download size={14} aria-hidden="true" />}
          Dokümanı Oluştur ve İndir
        </button>
        <span className="text-xs text-slate-400">
          {selected.length ? `${selected.length} özellik seçili` : 'En az bir özellik seçin'}
        </span>
      </div>
    </div>
  );
};

const IntegrationApi = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [requests, setRequests] = useState({ items: [], total: 0 });
  const [requestFilter, setRequestFilter] = useState({ key_ref: '', errors_only: false });
  const [requestPage, setRequestPage] = useState({ skip: 0, limit: 50 });
  const [docOpen, setDocOpen] = useState(false);
  const [docBusy, setDocBusy] = useState(false);

  const baseUrl = `${window.location.origin}/api/ext/v1`;
  // Panel adresi sabittir, giriş adresi değişir: Worker'a yazılacak adres budur.
  const entryEndpoint = `${baseUrl}/entry-domain`;

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await client.get('/admin/integration-keys');
      setData(response.data);
    } catch (requestError) {
      setError(errorText(requestError, 'Anahtarlar alınamadı'));
    }
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const response = await client.get('/admin/integration-keys/requests', {
        params: {
          ...requestPage,
          ...(requestFilter.key_ref ? { key_ref: requestFilter.key_ref } : {}),
          errors_only: requestFilter.errors_only,
        },
      });
      setRequests(response.data);
    } catch (requestError) {
      setRequests({ items: [], total: 0 });
    }
  }, [requestFilter, requestPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  const keyNames = useMemo(() => Object.fromEntries(
    (data?.items || []).map((item) => [item.id, item.name]),
  ), [data]);

  const save = async (form) => {
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      scopes: form.scopes,
      ip_allowlist: form.ip_allowlist.split(/[\n,]+/).map((item) => item.trim()).filter(Boolean),
      rate_limit_per_minute: Number(form.rate_limit_per_minute) || 120,
      daily_credit_limit: Number(form.daily_credit_limit) || 0,
    };
    try {
      if (form.id) {
        await client.patch(`/admin/integration-keys/${form.id}`, payload);
        toast({ title: 'Anahtar güncellendi' });
      } else {
        const response = await client.post('/admin/integration-keys', payload);
        setReveal({ ...response.data, rotated: false });
      }
      setEditing(null);
      await load();
    } catch (requestError) {
      toast({ title: 'Kaydedilemedi', description: errorText(requestError, 'Bir hata oluştu') });
    } finally {
      setBusy(false);
    }
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    setBusy(true);
    const { action, key } = confirm;
    try {
      if (action === 'rotate') {
        const response = await client.post(`/admin/integration-keys/${key.id}/rotate`);
        setReveal({ ...response.data, rotated: true });
      } else if (action === 'delete') {
        await client.delete(`/admin/integration-keys/${key.id}`);
        toast({ title: 'Anahtar silindi', description: `${key.name} artık kullanılamaz.` });
      } else if (action === 'toggle') {
        await client.patch(`/admin/integration-keys/${key.id}`, { active: !key.active });
        toast({ title: key.active ? 'Anahtar pasife alındı' : 'Anahtar yeniden açıldı' });
      }
      await load();
      await loadRequests();
    } catch (requestError) {
      toast({ title: 'İşlem yapılamadı', description: errorText(requestError, 'Bir hata oluştu') });
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  // Doküman seçime göre sunucuda üretilir; hazır bir dosya yoktur.
  const downloadDocs = async (features) => {
    if (!features.length) {
      toast({ title: 'Doküman oluşturulamadı', description: 'En az bir özellik seçin.' });
      return;
    }
    setDocBusy(true);
    try {
      const response = await client.get('/admin/integration-keys/docs', {
        params: { features: features.join(','), origin: window.location.origin },
        responseType: 'blob',
      });
      const disposition = response.headers?.['content-disposition'] || '';
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || 'entegrasyon-api-v1.html';
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: 'Doküman indirildi', description: `${features.length} özellik` });
    } catch (requestError) {
      // Hata cevabı da blob olarak gelir; metni okunup gösterilir.
      let description = 'Bir hata oluştu';
      try {
        const detail = JSON.parse(await requestError.response.data.text()).detail;
        if (typeof detail === 'string') description = detail;
      } catch (parseError) {
        // Gövde okunamadıysa genel metin kalır.
      }
      toast({ title: 'Doküman indirilemedi', description });
    } finally {
      setDocBusy(false);
    }
  };

  if (error && !data) return <AdminError message={error} onRetry={load} />;
  if (!data) return <AdminSkeleton rows={4} label="Entegrasyon anahtarları yükleniyor" />;

  const scopeLabel = Object.fromEntries(data.scopes.map((scope) => [scope.key, scope.label]));
  const confirmCopy = {
    rotate: {
      title: 'Sırrı yenile',
      description: 'Yeni bir sır üretilir. Eski sır 24 saat daha kabul edilir; dış ekip bu sürede yeni sırra geçmelidir.',
      label: 'Yenile',
      tone: 'primary',
    },
    delete: {
      title: 'Anahtarı sil',
      description: 'Anahtar kalıcı olarak silinir ve bu anahtarla gelen her istek reddedilir. İstek geçmişi silinmez.',
      label: 'Sil',
      tone: 'danger',
    },
    toggle: confirm?.key?.active ? {
      title: 'Anahtarı pasife al',
      description: 'Anahtar silinmez ama bu anahtarla gelen istekler reddedilir. İstediğiniz zaman yeniden açabilirsiniz.',
      label: 'Pasife al',
      tone: 'danger',
    } : {
      title: 'Anahtarı aç',
      description: 'Anahtar yeniden istek kabul eder.',
      label: 'Aç',
      tone: 'primary',
    },
  }[confirm?.action] || {};

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent-soft)', color: 'var(--accent-primary)' }}>
          <Plug size={18} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <h1 className="text-xl font-bold">Entegrasyon API</h1>
          <p className="text-xs text-slate-400">
            CRM gibi dış sistemlerin panele girmeden üye verisini okuması ve sınırlı işlem yapması için anahtarlar.
          </p>
        </div>
        <button
          type="button"
          className="admin-btn-secondary flex items-center gap-2"
          onClick={() => setDocOpen((open) => !open)}
          aria-expanded={docOpen}
        >
          <FileText size={14} aria-hidden="true" /> Doküman oluştur
        </button>
        {!editing && (
          <button type="button" className="admin-btn-primary flex items-center gap-2" onClick={() => setEditing({ ...emptyForm })}>
            <Plus size={14} /> Yeni anahtar
          </button>
        )}
      </div>

      <div className="admin-card p-4 text-xs text-slate-400 space-y-1">
        <div>
          Temel adres:{' '}
          <code className="font-mono" style={{ color: 'var(--admin-text)' }}>{baseUrl}</code>
          <button type="button" className="ml-2 align-middle" onClick={() => copyText(baseUrl, toast, 'Adres')} aria-label="Temel adresi kopyala">
            <Copy size={12} aria-hidden="true" />
          </button>
        </div>
        <div>
          Her istek anahtar kimliği, zaman damgası ve HMAC imzası taşır; yalnızca anahtara yazılan IP
          adreslerinden kabul edilir. API'den yapılan her işlem kayıtlarda “API: anahtar adı” olarak görünür.
          Ayrıntılar “Doküman oluştur” ile indirilen belgededir.
        </div>
      </div>

      <div className="admin-card p-4 text-xs text-slate-400 space-y-2">
        <h2 className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
          Güncel giriş adresi (Cloudflare Worker)
        </h2>
        <div>
          Oyuncu adresi değiştikçe ziyaretçiyi kendi Cloudflare Worker'ında yeni adrese yönlendiren
          ekip, yayındaki adresi bu uçtan okur. Giriş Adresleri'nde <b>Yayına al</b> ile yaptığınız her
          geçiş Worker'ın önbellek süresi dolunca (birkaç dakika) ona da yansır.
        </div>
        <div>
          Uç:{' '}
          <code className="font-mono break-all" style={{ color: 'var(--admin-text)' }}>GET {entryEndpoint}</code>
          <button type="button" className="ml-2 align-middle" onClick={() => copyText(entryEndpoint, toast, 'Uç adresi')} aria-label="Güncel adres ucunu kopyala">
            <Copy size={12} aria-hidden="true" />
          </button>
        </div>
        <ol className="list-decimal pl-5 space-y-1">
          <li><b>Yeni anahtar</b> → yalnızca <b>Giriş adresi</b> yetkisi. IP listesini boş bırakın (Worker'ın sabit IP'si yoktur).</li>
          <li>Anahtar kimliğini ve sırrı Worker'ı yazan ekibe verin.</li>
          <li>Anahtarın satırındaki <b>Doküman</b> ile belgeyi indirip aynı ekibe verin; imza kuralı ve örnek Worker kodu içindedir.</li>
        </ol>
        <div>
          Bu ekranı panelin sabit alan adından açın: uç adresi ve doküman o adresle yazılır. Oyuncu adresiyle
          verilen uç, adres değiştiğinde çalışmaz.
        </div>
      </div>

      {docOpen && (
        <DocBuilder
          catalog={data.doc_features || []}
          keys={data.items}
          busy={docBusy}
          onClose={() => setDocOpen(false)}
          onDownload={downloadDocs}
        />
      )}

      {reveal && (
        <SecretCard
          reveal={reveal}
          toast={toast}
          onClose={() => setReveal(null)}
          onDocs={reveal.scopes?.length
            ? () => downloadDocs(featuresForScopes(data.doc_features || [], reveal.scopes))
            : null}
        />
      )}

      {editing && (
        <KeyForm
          key={editing.id || 'new'}
          initial={editing}
          scopes={data.scopes}
          limits={data.limits}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSubmit={save}
        />
      )}

      {data.items.length === 0 && !editing && (
        <div className="admin-card p-6 text-sm text-slate-400">
          Henüz anahtar yok. “Yeni anahtar” ile dış sistem için bir anahtar oluşturun.
        </div>
      )}

      <div className="grid gap-3">
        {data.items.map((item) => (
          <div key={item.id} className="admin-card p-4">
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-[240px]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{item.name}</span>
                  <span className={`badge-pill ${item.active ? 'badge-green' : 'badge-gray'}`}>
                    {item.active ? 'Aktif' : 'Pasif'}
                  </span>
                  {item.previous_secret_valid_until && (
                    <span className="badge-pill badge-yellow" title="Sır yenilendi; eski sır bu ana kadar geçerli">
                      Eski sır {formatDateTime(item.previous_secret_valid_until)} tarihine kadar geçerli
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-slate-400 font-mono break-all">
                  {item.key_id}
                  <button type="button" className="ml-2 align-middle" onClick={() => copyText(item.key_id, toast, 'Anahtar kimliği')} aria-label="Anahtar kimliğini kopyala">
                    <Copy size={12} aria-hidden="true" />
                  </button>
                  <span className="ml-2">sır: …{item.secret_hint}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {item.scopes.map((scope) => (
                    <span key={scope} className={`badge-pill ${isWriteScope(scope) ? 'badge-yellow' : 'badge-gray'}`}>
                      {scopeLabel[scope] || scope}
                    </span>
                  ))}
                </div>
                <div className="mt-2 grid gap-1 text-xs text-slate-400 sm:grid-cols-2">
                  <div>IP: <span className="font-mono">{item.ip_allowlist.length ? item.ip_allowlist.join(', ') : 'her yerden (yalnızca güncel adres)'}</span></div>
                  <div>Sınır: {item.rate_limit_per_minute}/dk · Günlük tutar: {item.daily_credit_limit > 0 ? `${formatMoney(item.credit_used_today)} / ${formatMoney(item.daily_credit_limit)}` : 'sınırsız'}</div>
                  <div>Son 24 saat: {item.stats_24h.requests} istek{item.stats_24h.errors ? `, ${item.stats_24h.errors} hata` : ''}</div>
                  <div>Son kullanım: {item.last_used_at ? `${formatDateTime(item.last_used_at)} · ${item.last_used_ip}` : 'henüz yok'}</div>
                  <div>Oluşturan: {item.created_by_username || '-'} · {formatDateTime(item.created_at)}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="admin-btn-secondary flex items-center gap-1.5"
                  onClick={() => setEditing({
                    id: item.id,
                    name: item.name,
                    scopes: item.scopes,
                    ip_allowlist: item.ip_allowlist.join('\n'),
                    rate_limit_per_minute: item.rate_limit_per_minute,
                    daily_credit_limit: item.daily_credit_limit,
                  })}
                >
                  <Pencil size={14} /> Düzenle
                </button>
                <button
                  type="button"
                  className="admin-btn-secondary flex items-center gap-1.5"
                  disabled={docBusy}
                  onClick={() => downloadDocs(featuresForScopes(data.doc_features || [], item.scopes))}
                  title="Bu anahtarın yetkilerini anlatan dokümanı indirir"
                >
                  <FileText size={14} aria-hidden="true" /> Doküman
                </button>
                <button type="button" className="admin-btn-secondary flex items-center gap-1.5" onClick={() => setConfirm({ action: 'rotate', key: item })}>
                  <RefreshCw size={14} /> Sırrı yenile
                </button>
                <button type="button" className="admin-btn-secondary flex items-center gap-1.5" onClick={() => setConfirm({ action: 'toggle', key: item })}>
                  <Power size={14} /> {item.active ? 'Pasife al' : 'Aç'}
                </button>
                <button type="button" className="admin-btn-secondary flex items-center gap-1.5 text-red-400" onClick={() => setConfirm({ action: 'delete', key: item })}>
                  <Trash2 size={14} /> Sil
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-card p-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="font-semibold flex-1 min-w-[160px]">Son İstekler</div>
          <select
            className="admin-input admin-input-w-md"
            aria-label="Anahtara göre süz"
            value={requestFilter.key_ref}
            onChange={(event) => {
              setRequestPage((prev) => ({ ...prev, skip: 0 }));
              setRequestFilter((prev) => ({ ...prev, key_ref: event.target.value }));
            }}
          >
            <option value="">Bütün anahtarlar</option>
            {data.items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={requestFilter.errors_only}
              onChange={(event) => {
                setRequestPage((prev) => ({ ...prev, skip: 0 }));
                setRequestFilter((prev) => ({ ...prev, errors_only: event.target.checked }));
              }}
            />
            Yalnızca hatalar
          </label>
          <button type="button" className="admin-btn-secondary" onClick={loadRequests} aria-label="Listeyi yenile">
            <RefreshCw size={14} aria-hidden="true" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="admin-table w-full min-w-[820px] tabular-nums">
            <thead>
              <tr><th>ZAMAN</th><th>ANAHTAR</th><th>İSTEK</th><th>ÜYE</th><th>SONUÇ</th><th>IP</th><th>SÜRE</th></tr>
            </thead>
            <tbody>
              {requests.items.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-500 py-6">Kayıt yok</td></tr>
              )}
              {requests.items.map((row) => (
                <tr key={row.id}>
                  <td className="whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                  <td>{keyNames[row.key_ref] || row.key_name}</td>
                  <td className="font-mono text-xs">{row.method} {row.path.replace('/api/ext/v1', '')}</td>
                  <td className="font-mono text-xs">{row.client_id ? `${row.client_id.slice(0, 8)}…` : '-'}</td>
                  <td>
                    <span className={`badge-pill ${row.status_code < 400 ? 'badge-green' : 'badge-red'}`}>{row.status_code}</span>
                    {row.error && <span className="ml-2 text-xs text-slate-400">{ERROR_LABELS[row.error] || row.error}</span>}
                  </td>
                  <td className="font-mono text-xs">{row.ip}</td>
                  <td className="text-xs">{row.duration_ms} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-xs text-slate-500">
          <span>
            {requests.total} kayıt · son {data.limits.request_log_days} gün tutulur
          </span>
          <div className="flex items-center gap-2">
            <PageSizeSelect
              value={requestPage.limit}
              onChange={(limit) => setRequestPage({ skip: 0, limit })}
            />
            <button
              type="button"
              className="admin-btn-secondary disabled:opacity-40"
              disabled={requestPage.skip === 0}
              onClick={() => setRequestPage((prev) => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
            >
              Önceki
            </button>
            <button
              type="button"
              className="admin-btn-secondary disabled:opacity-40"
              disabled={requestPage.skip + requestPage.limit >= requests.total}
              onClick={() => setRequestPage((prev) => ({ ...prev, skip: prev.skip + prev.limit }))}
            >
              Sonraki
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmLabel={confirmCopy.label}
        tone={confirmCopy.tone}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirmed}
      />
    </div>
  );
};

export default IntegrationApi;
